import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { MultiSelect } from "@/components/ui/multi-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type ApiError, isApiWaitingOrError } from "@/data-interfaces/generics";
import type { RealTimeData } from "@/data-interfaces/real-time-data";
import type { LibraryTool, Rack, UsedTool } from "@/data-interfaces/tools";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import {
	getLibraryTools,
	getMaterialOptions,
	getToolPositions,
	getUsedTools,
} from "@/lib/data-remappings/tools";
import { getAppWrappedPageUrl, TOOLS_PAGE_URL } from "@/lib/page-routes";
import {
	getRtdSubscriptionTopic,
	TL_LIBRARY_TOOLS_URL,
	TL_RACK_POCKETS_URL,
	TL_USED_TOOLS_URL,
} from "@/lib/request-paths";
import { getMachineSerialNumber } from "@/lib/session-storage-keys";

export const Route = createFileRoute(getAppWrappedPageUrl(TOOLS_PAGE_URL))({
	component: Tools,
	validateSearch: (search: Record<string, unknown>) => {
		return search.selectedTab ?? { selectedTab: "racks" };
	},
});

function Tools() {
	const [t, _] = useTranslation();

	const { selectedTab } = Route.useSearch();

	const navigate = useNavigate();

	const [selectedMaterials, setSelectedMaterials] = useState<Array<string>>([]);

	const [racks, setRacks] = useState<Array<Rack> | ApiError | null>(null);
	const [usedTools, setUsedTools] = useState<Array<UsedTool> | ApiError | null>(
		null,
	);
	const [libraryTools, setLibraryTools] = useState<
		Array<LibraryTool> | ApiError | null
	>(null);
	const [realTimeData, setRealTimeData] = useState<
		RealTimeData | ApiError | null
	>(null);

	useMQTTSubscribe(TL_RACK_POCKETS_URL, setRacks);
	useMQTTSubscribe(TL_USED_TOOLS_URL, setUsedTools);
	useMQTTSubscribe(TL_LIBRARY_TOOLS_URL, setLibraryTools);
	useMQTTSubscribe(
		getRtdSubscriptionTopic(getMachineSerialNumber()),
		setRealTimeData,
		true,
	);

	useMQTTAutoRefreshPublish(30, TL_RACK_POCKETS_URL);
	useMQTTAutoRefreshPublish(30, TL_USED_TOOLS_URL);
	useMQTTAutoRefreshPublish(30, TL_LIBRARY_TOOLS_URL);

	return (
		<div className="text-white p-8">
			<h1 className="text-3xl sm:text-4xl font-medium">{t("Tools")}</h1>

			<Tabs defaultValue={selectedTab} className="flex flex-1 gap-y-8 mt-8">
				<div className="flex flex-1 flex-row justify-center">
					<TabsList>
						<TabsTrigger value="racks" className="min-w-25">
							{t("Racks")}
						</TabsTrigger>
						<TabsTrigger value="used" className="min-w-25">
							{t("Used")}
						</TabsTrigger>
						<TabsTrigger value="library" className="min-w-25">
							{t("Library")}
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent value="racks" className="flex flex-col gap-y-4">
					<MqttSuspense
						objects={racks}
						requestUrl={TL_RACK_POCKETS_URL}
						genericError="Error retrieving racks"
					>
						{(racks) => (
							<Accordion type="single" collapsible className="w-full">
								{racks.map((rp) => {
									return (
										<AccordionItem key={rp.name} value={rp.name}>
											<AccordionTrigger className="text-2xl sm:text-3xl">
												<div className="flex flex-row items-center gap-x-2">
													<div>{rp.name}</div>

													{!isApiWaitingOrError(realTimeData) &&
														rp.name === realTimeData.rack && (
															<div className="h-9 w-9 flex items-center justify-center text-[#FFF394]">
																<Check className="w-full h-full" />
															</div>
														)}
												</div>
											</AccordionTrigger>

											<AccordionContent className="flex flex-col">
												<MultiPropertyCardList
													multiPropertyObjects={getToolPositions(
														rp.name,
														rp.pockets,
														realTimeData,
														navigate,
													)}
													noItemsLabel="No tool positioned in rack"
												/>
											</AccordionContent>
										</AccordionItem>
									);
								})}
							</Accordion>
						)}
					</MqttSuspense>
				</TabsContent>

				<TabsContent value="used">
					<MqttSuspense
						objects={usedTools}
						requestUrl={TL_USED_TOOLS_URL}
						genericError="Error retrieving tools"
					>
						{(usedTools) => (
							<MultiPropertyCardList
								multiPropertyObjects={getUsedTools(usedTools, navigate)}
								noItemsLabel="No used tool"
							/>
						)}
					</MqttSuspense>
				</TabsContent>

				<TabsContent value="library" className="flex flex-col gap-y-4">
					<MqttSuspense
						objects={libraryTools}
						requestUrl={TL_LIBRARY_TOOLS_URL}
						genericError="Error retrieving tools"
					>
						{(libraryTools) => (
							<>
								<div className="flex flex-col sm:flex-row items-center max-w-125">
									<span className="mb-2 sm:mb-0 sm:mr-2">
										{t("Materials")}:{" "}
									</span>
									<MultiSelect
										options={getMaterialOptions(libraryTools)}
										onValueChange={setSelectedMaterials}
										autoSize={true}
									/>
								</div>

								<MultiPropertyCardList
									multiPropertyObjects={getLibraryTools(
										libraryTools,
										realTimeData,
										navigate,
										selectedMaterials,
									)}
									noItemsLabel="No library tool"
									pagination={true}
								/>
							</>
						)}
					</MqttSuspense>
				</TabsContent>
			</Tabs>
		</div>
	);
}
